import tippy from 'tippy.js';
import { Sidebar } from '../modules/Sidebar';

class Minicart {
    constructor() {
        this.sidebar = null;
        this.contentHost = null;
        this.mode = 'header';
        this.highlightCartItemKey = '';
        this.sourceProductId = 0;
        this.quantityTimer = null;
        this.isLoading = false;
        this.cartDirty = true;
        this.pendingQuantityKey = '';

        if (typeof ajax === 'undefined') {
            return;
        }

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.init());
            return;
        }

        this.init();
    }

    init() {
        this.sidebar = new Sidebar({
            overlayClassNames: ['bg-grey-900/60'],
        });

        this.contentHost = document.createElement('div');
        this.sidebar.content.appendChild(this.contentHost);

        document.addEventListener('click', this.onDocumentClick.bind(this), true);
        document.addEventListener('change', this.onDocumentChange.bind(this), true);
        this.sidebar.content.addEventListener('click', this.onSidebarClick.bind(this));
        this.sidebar.content.addEventListener('change', this.onSidebarChange.bind(this));

        this.bootstrapInitialSidebar();

        window.mdaMinicart = this;
    }

    bootstrapInitialSidebar() {
        const template = document.getElementById('mda-minicart-initial-sidebar');

        if (!template) {
            return;
        }

        const html = template.innerHTML.trim();

        if (html === '') {
            return;
        }

        this.applySidebarHtml(html, {
            mode: 'header',
            sourceProductId: this.getPageProductId(),
        });
    }

    getPageProductId() {
        const productRoot = document.querySelector('[id^="product-"]');

        if (!productRoot) {
            return 0;
        }

        const productId = parseInt(productRoot.id.replace('product-', ''), 10);

        return Number.isNaN(productId) ? 0 : productId;
    }

    resolveFormProductId(form, formData) {
        const addToCartField = form.querySelector('[name="add-to-cart"]');
        const addToCartValue =
            addToCartField && 'value' in addToCartField
                ? String(addToCartField.value || '')
                : '';

        const productId = parseInt(
            form.dataset.product_id ||
                formData.get('product_id') ||
                addToCartValue ||
                formData.get('add-to-cart') ||
                String(this.getPageProductId() || 0),
            10
        );

        return Number.isNaN(productId) ? 0 : productId;
    }

    isInspirationSidebarContext(node) {
        return Boolean(node?.closest?.('[data-inspiration-sidebar]'));
    }

    isInspirationSidebarOpen() {
        return Boolean(
            window.mdaInspirationsPage?.inspirationSidebar?.root?.classList.contains(
                '-active'
            )
        );
    }

    findCartItemElement(cartItemKey) {
        if (!cartItemKey) {
            return null;
        }

        return document.querySelector(
            `[data-minicart-cart-item][data-cart-item-key="${cartItemKey}"]`
        );
    }

    async refreshInspirationShoppingListIfOpen() {
        await window.mdaInspirationsPage?.refreshActiveInspirationSidebar?.();
    }

    shouldRefreshInspirationSidebar(cartItemKey = '') {
        if (!this.isInspirationSidebarOpen()) {
            return false;
        }

        if (!cartItemKey) {
            return true;
        }

        const cartItem = this.findCartItemElement(cartItemKey);

        return this.isInspirationSidebarContext(cartItem);
    }

    initTooltips(root = this.contentHost) {
        if (!root) {
            return;
        }

        root.querySelectorAll('[js-tippy]').forEach(element => {
            if (element._tippy) {
                return;
            }

            tippy(element, {
                content: element.getAttribute('js-tippy'),
                allowHTML: true,
            });
        });
    }

    initSidebarEnhancements(root = this.contentHost) {
        this.initTooltips(root);

        if (typeof window.mdaInitQuantityInputs === 'function') {
            window.mdaInitQuantityInputs(root);
        }
    }

    showSidebarLoader() {
        if (!this.contentHost) {
            return;
        }

        this.contentHost.innerHTML =
            '<div class="minicart-sidebar__loader flex items-center justify-center px-24 py-48 text-1614! text-text-secondary">Ładowanie...</div>';
    }

    applySidebarHtml(html, state = {}) {
        if (!this.contentHost) {
            return;
        }

        this.mode = state.mode || this.mode;
        this.highlightCartItemKey = state.highlightCartItemKey || '';
        this.sourceProductId = state.sourceProductId || this.sourceProductId;
        this.contentHost.innerHTML = html || '';
        this.cartDirty = false;
        this.initSidebarEnhancements();
    }

    markCartDirty() {
        this.cartDirty = true;
    }

    canReuseSidebarCache() {
        return (
            !this.cartDirty &&
            this.mode === 'header' &&
            Boolean(this.contentHost?.innerHTML.trim())
        );
    }

    async request(type, payload = {}) {
        const formData = new FormData();
        formData.append('action', 'mda_minicart_action');
        formData.append('type', type);
        formData.append('nonce', ajax.nonce);

        Object.entries(payload).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                formData.append(key, String(value));
            }
        });

        const response = await fetch(ajax.url, {
            method: 'POST',
            body: formData,
        });

        const result = await response.json();

        if (!result?.success) {
            throw new Error(result?.data?.message || 'Minicart request failed');
        }

        return result.data;
    }

    async renderSidebar(payload = {}) {
        const data = await this.request('sidebar', {
            mode: payload.mode || this.mode,
            cart_item_key: payload.cart_item_key || this.highlightCartItemKey,
            source_product_id:
                payload.source_product_id ||
                this.sourceProductId ||
                this.getPageProductId(),
        });

        this.applySidebarHtml(data.html || '', {
            mode: payload.mode || this.mode,
            highlightCartItemKey: payload.cart_item_key || this.highlightCartItemKey,
            sourceProductId:
                payload.source_product_id ||
                this.sourceProductId ||
                this.getPageProductId(),
        });
    }

    async loadHtml(html, state = {}) {
        this.applySidebarHtml(html, state);
    }

    async openFromHeader() {
        if (this.isLoading) {
            return;
        }

        this.bootstrapInitialSidebar();

        if (this.canReuseSidebarCache()) {
            this.sidebar.open();
            return;
        }

        this.isLoading = true;
        this.sidebar.open();
        this.showSidebarLoader();

        try {
            this.mode = 'header';
            this.highlightCartItemKey = '';
            this.sourceProductId = this.getPageProductId();

            await this.renderSidebar({
                mode: 'header',
                source_product_id: this.sourceProductId,
            });
        } catch (error) {
            console.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    async openAfterAdd(html, state = {}) {
        await this.loadHtml(html, {
            mode: 'after_add',
            ...state,
        });
        this.sidebar.open();
    }

    async addFromProductForm(form) {
        if (this.isLoading || !(form instanceof HTMLFormElement)) {
            return;
        }

        const formData = new FormData(form);
        const isVariationForm = form.classList.contains('variations_form');
        const parentProductId = this.resolveFormProductId(form, formData);
        let variationId = parseInt(formData.get('variation_id') || '0', 10);
        const quantity = Math.max(1, parseInt(formData.get('quantity') || '1', 10));

        if (isVariationForm && !variationId && typeof window.mdaAutoSelectSingleVariation === 'function') {
            window.mdaAutoSelectSingleVariation(form);
            variationId = parseInt(form.querySelector('[name="variation_id"]')?.value || '0', 10);
        }

        const payload = {
            product_id: parentProductId,
            variation_id: isVariationForm ? variationId : 0,
            quantity,
            source_product_id: parentProductId || this.getPageProductId(),
        };

        for (const [key, value] of formData.entries()) {
            if (key.startsWith('attribute_')) {
                payload[key] = value;
            }
        }

        this.isLoading = true;
        this.markCartDirty();

        try {
            const data = await this.request('add', payload);

            this.mode = 'after_add';
            this.highlightCartItemKey = data.cart_item_key || '';
            this.sourceProductId = payload.source_product_id;

            await this.openAfterAdd(data.html, {
                highlightCartItemKey: this.highlightCartItemKey,
                sourceProductId: this.sourceProductId,
            });
        } catch (error) {
            console.error(error);
            window.alert(error?.message || 'Nie udało się dodać produktu do koszyka.');
        } finally {
            this.isLoading = false;
        }
    }

    getCartItemQuantity(cartItemKey) {
        const item = this.findCartItemElement(cartItemKey);

        if (!item) {
            return 1;
        }

        const input = item.querySelector('[data-minicart-quantity] input');

        return Math.max(1, parseInt(input?.value || '1', 10));
    }

    getRecommendationQuantity(productId) {
        const item = document.querySelector(
            `[data-product-id="${productId}"][data-minicart-recommendation]`
        );

        if (!item) {
            return 1;
        }

        const input = item.querySelector('[data-minicart-quantity] input');

        return Math.max(1, parseInt(input?.value || '1', 10));
    }

    async updateCartItem(cartItemKey, quantity) {
        if (this.pendingQuantityKey === cartItemKey) {
            return;
        }

        const cartItem = this.findCartItemElement(cartItemKey);
        const refreshInspiration = this.shouldRefreshInspirationSidebar(cartItemKey);

        this.pendingQuantityKey = cartItemKey;
        cartItem?.classList.add('opacity-60', 'pointer-events-none');
        this.markCartDirty();

        try {
            const data = await this.request('update', {
                cart_item_key: cartItemKey,
                quantity,
                mode: this.mode,
                highlight_cart_item_key: this.highlightCartItemKey,
            });

            if (refreshInspiration) {
                await this.refreshInspirationShoppingListIfOpen();
            } else {
                await this.loadHtml(data.html, {
                    mode: this.mode,
                    highlightCartItemKey: this.highlightCartItemKey,
                    sourceProductId: this.sourceProductId,
                });
            }
        } finally {
            this.pendingQuantityKey = '';
            cartItem?.classList.remove('opacity-60', 'pointer-events-none');
        }
    }

    async removeCartItem(cartItemKey) {
        const refreshInspiration = this.shouldRefreshInspirationSidebar(cartItemKey);

        this.markCartDirty();

        const data = await this.request('remove', {
            cart_item_key: cartItemKey,
            mode: this.mode,
            highlight_cart_item_key: this.highlightCartItemKey,
        });

        if (this.highlightCartItemKey === cartItemKey) {
            this.mode = 'header';
            this.highlightCartItemKey = '';
        }

        if (refreshInspiration) {
            await this.refreshInspirationShoppingListIfOpen();
            return;
        }

        await this.loadHtml(data.html, {
            mode: this.mode,
            highlightCartItemKey: this.highlightCartItemKey,
            sourceProductId: this.sourceProductId,
        });

        if (!this.contentHost.querySelector('[data-minicart-cart-item]')) {
            this.mode = 'header';
            this.highlightCartItemKey = '';
        }
    }

    async addRecommendation(productId, variationId = 0, options = {}) {
        if (this.isLoading || productId <= 0) {
            return;
        }

        const inInspiration = options.inInspiration === true;

        this.isLoading = true;
        this.markCartDirty();

        try {
            const data = await this.request('add', {
                product_id: productId,
                variation_id: variationId,
                quantity: 1,
                source_product_id: this.sourceProductId || this.getPageProductId(),
            });

            if (inInspiration) {
                await this.refreshInspirationShoppingListIfOpen();
                return;
            }

            this.mode = 'after_add';
            this.highlightCartItemKey = data.cart_item_key || '';

            await this.openAfterAdd(data.html, {
                highlightCartItemKey: this.highlightCartItemKey,
                sourceProductId: this.sourceProductId || this.getPageProductId(),
            });
        } catch (error) {
            console.error(error);
            window.alert(error?.message || 'Nie udało się dodać produktu do koszyka.');
        } finally {
            this.isLoading = false;
        }
    }

    handleAddRecommendationClick(button) {
        const productId = parseInt(button.getAttribute('data-product-id') || '0', 10);
        const variationId = parseInt(button.getAttribute('data-variation-id') || '0', 10);

        if (productId > 0) {
            this.addRecommendation(productId, variationId, {
                inInspiration: this.isInspirationSidebarContext(button),
            });
        }
    }

    onDocumentClick(event) {
        const addRecommendationButton = event.target.closest(
            '[data-minicart-add-recommendation]'
        );

        if (addRecommendationButton) {
            event.preventDefault();
            event.stopPropagation();
            this.handleAddRecommendationClick(addRecommendationButton);
            return;
        }

        const removeButton = event.target.closest('[data-minicart-remove-item]');

        if (removeButton && this.isInspirationSidebarContext(removeButton)) {
            event.preventDefault();
            event.stopPropagation();

            const cartItemKey = removeButton.getAttribute('data-cart-item-key');

            if (cartItemKey) {
                this.removeCartItem(cartItemKey).catch(console.error);
            }

            return;
        }

        const cartTrigger = event.target.closest('[js-trigger-menu-element="cart"]');

        if (!cartTrigger) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.openFromHeader();
    }

    onSidebarClick(event) {
        const continueButton = event.target.closest('.minicart-sidebar__continue-btn');
        if (continueButton) {
            event.preventDefault();
            this.sidebar.close();
            return;
        }

        const removeButton = event.target.closest('[data-minicart-remove-item]');
        if (removeButton) {
            event.preventDefault();
            const cartItemKey = removeButton.getAttribute('data-cart-item-key');

            if (cartItemKey) {
                this.removeCartItem(cartItemKey).catch(console.error);
            }
            return;
        }

    }

    onSidebarChange(event) {
        const input = event.target.closest('[data-minicart-quantity] input');

        if (!input) {
            return;
        }

        const cartItem = input.closest('[data-minicart-cart-item]');

        if (!cartItem || !this.contentHost?.contains(cartItem)) {
            return;
        }

        this.scheduleCartItemQuantityUpdate(cartItem, input);
    }

    onDocumentChange(event) {
        const input = event.target.closest('[data-minicart-quantity] input');

        if (!input) {
            return;
        }

        const cartItem = input.closest('[data-minicart-cart-item]');

        if (!cartItem || !this.isInspirationSidebarContext(cartItem)) {
            return;
        }

        this.scheduleCartItemQuantityUpdate(cartItem, input);
    }

    scheduleCartItemQuantityUpdate(cartItem, input) {
        const cartItemKey = cartItem.getAttribute('data-cart-item-key');

        if (!cartItemKey) {
            return;
        }

        clearTimeout(this.quantityTimer);

        this.quantityTimer = setTimeout(() => {
            const quantity = Math.max(0, parseInt(input.value || '0', 10));
            this.updateCartItem(cartItemKey, quantity).catch(console.error);
        }, 600);
    }
}

new Minicart();
